<template>
    <div class="c-header-actions__item is-minicart">
        <a
            href="/nakupny-kosik"
            class="c-link is-minicart"
            :class="{'is-active': isOpen}"
            :data-quantity="count"
            @click="handleMinicartClick"
        >
            <i class="ico ico-cart" aria-hidden="true"></i>
            <span class="is-text" v-if="isEmpty">{{ $t('Minicart.Trigger.title') }}</span>
            <span class="is-text" v-else v-html="formatPrice(pricesWithVat ? totalPrice : subtotalPrice)"></span>
            <div class="c-badge is-minicart" :data-count="count">{{ count }}</div>
        </a>

        <div :class="{
            'c-minicart': true,
            'is-loading': isLoading
        }" v-if="isOpen">
            <div class="c-minicart__products-wrapper">
                <div class="c-minicart__header" :class="{ 'is-initializing': !isInitialized && isLoading }">
                    <div class="c-minicart__title">
                        {{ $t('Minicart.Header.title') }}
                    </div>

                    <div class="c-minicart__msg" v-if="isInitialized && !isEmpty">
                        <div class="c-msg c-msg--info" v-if="isInitialized && !isEmpty">
                            <i class="ico ico-truck"></i>
                            <span v-html="freeShippingText"></span>
                        </div>
                    </div>
                </div>

                <template v-if="isInitialized">
                    <div class="c-minicart__products" v-if="!isEmpty">
                        <MinicartItem
                            v-for="item in items"
                            :key="item.cart_id"
                            :item="item"
                            :isUpdatingQuantity="updatingQuantity[item.cart_id]"
                            @remove="removeItem"
                            @spinningQuantity="spinningQuantity"
                            @updateQuantity="updateQuantity"
                        />
                    </div>

                    <div class="c-minicart__empty" v-else>
                        {{ $t('Minicart.emptyText') }}
                    </div>
                </template>
            </div>

            <div class="c-minicart__subtotal" v-if="isInitialized && !isEmpty">
                <div class="c-minicart-subtotal">
                    <span class="c-minicart-subtotal__key is-bigger">
                        {{ $t('Minicart.priceWithVat') }}
                    </span>
                    <span
                        class="c-minicart-subtotal__value is-bigger"
                        v-html="formatPrice(totalPrice)"
                    ></span>

                    <span class="c-minicart-subtotal__key">
                        {{ $t('Minicart.priceWithoutVat') }}
                    </span>
                    <span
                        class="c-minicart-subtotal__value"
                        v-html="formatPrice(subtotalPrice)"
                    ></span>

                    <template v-if="savings > 0">
                        <span class="c-minicart-subtotal__key h-is-secondary">
                            {{ $t('Minicart.savings') }}
                        </span>
                        <span
                            class="c-minicart-subtotal__value h-is-secondary"
                            v-html="formatPrice(savings)"
                        ></span>
                    </template>
                </div>

                <a class="c-btn c-btn--cart" href="/nakupny-kosik" :title="$t('Minicart.proceedToCheckout')">
                    {{ $t('Minicart.proceedToCheckout') }}
                    <i aria-hidden="true" class="ico ico-arrow-right is-on-right"></i>
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import { showHeaderOverlay, hideHeaderOverlay } from 'Functions/overlay';
import RshopMinicart from "FrontendComponents/Minicart";
import { formatPrice } from "FrontendJsAssets/common/format";
import client from "FrontendJsAssets/common/client";
import { CancelToken } from "axios";
import { checkResolution } from 'Functions/helpers';

const lang = document.querySelector('html').getAttribute('lang');
let locale;

import(`../../locale/${lang}`).then((module) => {
    locale = module.Swal;
});

export default {
    extends: RshopMinicart,
    components: {
        MinicartItem: () => import("./MinicartItem"),
    },
    created() {
        this.fetchCart();

        if (typeof Event.on === 'function') {
            Event.on('Cart.product.after_add', (response) => {
                this.fetchCart();
            });

            Event.on('Cart.addedProduct', (response) => {
                this.fetchCart();
            });

            Event.on('Cart.customFetchCart', () => {
                this.fetchCart();
            });

            Event.on('Cart.customCancelFetchCart', () => {
                this.cancelFetchCart();
            });
        }
    },
    data() {
        return {
            isOpen: false,
            pricesWithVat: window.Eshop.options.pricesWithVat,
            freeShipping: [],
            items: [],
            totals: [],
            fetchCancelToken: null,
            isInitialized: false,
            fetchingProducts: true,
            fetchingTotals: true,
            updatingQuantity: {},
            updatingQuantityTimeouts: {},
            updateQuantityCancelTokens: {},
        };
    },
    computed: {
        count() {
            return this.totals.count || 0
        },
        itemsCount() {
            if (this.items) {
                return this.items.reduce((count, item) => count + parseInt(item.quantity), 0)
            }

            return 0
        },
        isEmpty() {
            return this.itemsCount === 0
        },
        totalPrice() {
            return this.totals.total
        },
        subtotalPrice() {
            return this.totals.subtotal
        },
        savings() {
            return Math.abs(this.totals.discount)
        },
        hasFreeShipping() {
            return this.freeShipping ? this.freeShipping.free_shipping_amount <= 0 : false
        },
        freeShippingText() {
            if (!this.freeShipping) {
                return null
            }

            return this.freeShipping[this.hasFreeShipping ? 'free_shipping_text' : 'not_free_shipping_text']
        },
        isLoading() {
            return this.fetchingProducts || this.fetchingTotals || Object.keys(this.updatingQuantity).length > 0
        }
    },
    methods: {
        cancelFetchCart() {
            if (this.fetchCancelToken) {
                this.fetchCancelToken.cancel()
            }

            this.fetchCancelToken = CancelToken.source()
        },
        fetchCart() {
            this.cancelFetchCart()
            this.fetchProducts()
            this.fetchTotals()
        },
        fetchProducts() {
            this.fetchingProducts = true

            client
                .get("/cart/fetch-products", {
                    cancelToken: this.fetchCancelToken.token,
                })
                .then((response) => {
                    if (!response.data.status) {
                        return;
                    }

                    this.items = Object.values(response.data.data.items);

                    Event.dispatch('Cart.fetchedProducts', {
                        response: response.data
                    });
                })
                .catch((error) => {})
                .then(() => {
                    this.fetchingProducts = false
                });
        },
        fetchTotals() {
            this.fetchingTotals = true

            client
                .get("/cart/fetch-totals", {
                    cancelToken: this.fetchCancelToken.token,
                })
                .then((response) => {
                    if (!response.data.status) {
                        return;
                    }

                    this.freeShipping = response.data.data.free_shipping;
                    this.totals = response.data.data.totals;

                    Event.dispatch('Custom.Cart.fetchedTotals', {
                        response: response.data
                    });
                })
                .catch((error) => {})
                .then(() => {
                    this.fetchingTotals = false
                });
        },
        formatPrice(price) {
            return formatPrice(price);
        },
        removeItem(cartId) {
            let itemIndex = -1;

            // find item
            const item = this.items.find((item, index) => {
                if (item.cart_id === cartId) {
                    itemIndex = index;
                    return true;
                }

                return false;
            });

            if (itemIndex === -1) {
                return false;
            }

            // remove item
            window.Eshop.confirm(
                locale.DeleteProduct.title,
                {
                    denyButtonText: locale.DeleteProduct.deny,
                    confirmButtonText: locale.DeleteProduct.confirm
                },
                () => {
                    document.querySelector('.swal2-confirm').classList.add('is-loading');

                    return client.post(`/remove-item/${cartId}`).then((response) => {
                        window.Event.dispatch('Cart.product.removed', response.data);
                        window.Event.dispatch('Cart.removedProduct', {
                            response: response.data,
                            product: item
                        });
                    });
                },
                (result) => {
                    if (result.isConfirmed) {
                        this.items.splice(itemIndex, 1)
                        this.fetchCart()
                    }
                }
            );
        },
        spinningQuantity(cartId) {
            if (!this.updatingQuantity[cartId]) {
                return
            }

            this.$set(this.updatingQuantity, cartId, true)
            this.cancelUpdateQuantity(cartId)
            this.cancelFetchCart()

            if (this.updatingQuantityTimeouts[cartId]) {
                clearTimeout(this.updatingQuantityTimeouts[cartId])
            }
        },
        cancelUpdateQuantity(cartId) {
            const quantityCancelToken = this.updateQuantityCancelTokens[cartId]

            if (quantityCancelToken) {
                quantityCancelToken.cancel()
            }

            this.updateQuantityCancelTokens[cartId] = CancelToken.source()
        },
        updateQuantity(cartId, quantity) {
            const isFirstUpdate = !this.updatingQuantity[cartId]

            this.$set(this.updatingQuantity, cartId, true)
            this.cancelUpdateQuantity(cartId)
            this.cancelFetchCart()

            if (this.updatingQuantityTimeouts[cartId]) {
                clearTimeout(this.updatingQuantityTimeouts[cartId])
            }

            this.$set(this.updatingQuantityTimeouts, cartId, setTimeout(() => {
                this.postNewQuantity(cartId, quantity)

                this.$delete(this.updatingQuantityTimeouts, cartId)
            }, isFirstUpdate ? 0 : 650))
        },
        postNewQuantity(cartId, quantity) {
            client
                .post(`/change-item-quantity/${cartId}`, {
                    quantity: quantity
                },{
                    cancelToken: this.updateQuantityCancelTokens[cartId].token,
                })
                .then((response) => {
                    const data = response.data

                    if (!data.status) {
                        return;
                    }

                    this.items.forEach((item, index) => {
                        if (item.cart_id == cartId) {
                            this.$set(this.items, index, Object.assign(this.items[index], {
                                quantity: data.item.quantity,
                                price: data.item.price,
                                price_vat: data.item.price_vat,
                            }))
                        }
                    });

                    this.totals.total = data.totals.total
                    this.totals.subtotal = data.totals.subtotal
                    this.afterQuantityFetch(cartId)
                })
                .catch((error) => {
                    if (!client.isCancel(error)) {
                        this.afterQuantityFetch(cartId)
                    }
                });
        },
        afterQuantityFetch(cartId) {
            if (!this.updatingQuantityTimeouts[cartId]) {
                this.$delete(this.updatingQuantity, cartId)
            }

            if (!Object.keys(this.updatingQuantity).length) {
                this.fetchCart()
            }
        },
        handleClickOutside($event) {
            if (this.isOpen && !this.$el.contains($event.target) && !$event.target.closest('.swal2-container') && !$event.target.closest('.notify')) {
                this.toggleMinicart(false)
            }
        },
        handleMinicartClick(event) {
            if (!checkResolution('cxs;xs')) {
                event.preventDefault()
                this.toggleMinicart()
            }
        },
        toggleMinicart(bool) {
            if (typeof bool !== 'boolean') {
                bool = !this.isOpen
            }

            this.isOpen = bool

            if (bool) {
                document.addEventListener("click", this.handleClickOutside)
            } else {
                document.removeEventListener("click", this.handleClickOutside)
            }
        },
    },
    watch: {
        isOpen(value) {
            if (value) {
                showHeaderOverlay('minicart')
            } else {
                hideHeaderOverlay('minicart')
            }
        },
        isLoading(value) {
            if (!this.value && !this.isInitialized) {
                this.isInitialized = true
            }
        }
    }
};
</script>
